// =============================================================================
// Pintravel — Brand Logo
// =============================================================================
// The mark is a map pin with a plane knocked out of its head. Two hard rules
// from the brand spec:
//
//   1. The plane is NEVER its own colour — it is always a cutout showing the
//      background behind the mark (white on light, navy on dark, coral inside
//      the app icon).
//   2. The mark stays coral when the theme flips; only the cutout changes.
//
// Rendered inline rather than via <img> so the cutout can follow the current
// surface colour. The geometry matches assets/brand/*.svg 1:1 — if you edit one,
// edit the other.
// =============================================================================

import { Box, useTheme, type SxProps, type Theme } from '@mui/material';

import { CORAL, CORAL_ON_DARK } from '@/theme/tokens';

// Pin silhouette. The stroke is intentional: it thickens and smooths the shape,
// so it must match the fill colour and keep linejoin round.
const PIN_PATH =
  'M50 88 C40 74 22 54 22 38 C22 22.5 34.5 10 50 10 C65.5 10 78 22.5 78 38 C78 54 60 74 50 88 Z';

// Plane, deliberately ~25% smaller than natural so the mark breathes.
const PLANE_PATH =
  'M50 20 C52.5 20 54 22 54 25 L54 35 L72 45 L72 50 L54 44 L54 56 L60 61 L60 65 L50 62 L40 65 L40 61 L46 56 L46 44 L28 50 L28 45 L46 35 L46 25 C46 22 47.5 20 50 20 Z';
const PLANE_TRANSFORM = 'translate(50,40) scale(0.75) translate(-50,-42.5)';

// -----------------------------------------------------------------------------
// LogoMark
// -----------------------------------------------------------------------------

export interface LogoMarkProps {
  /** Rendered width and height in px. */
  size?: number;
  /**
   * Pin colour. Defaults to coral — override only for the mono variants
   * (print, watermark) or when the mark sits on a coral surface.
   */
  color?: string;
  /**
   * Colour showing through the plane cutout. Must match whatever is actually
   * behind the mark. Defaults to the current theme's surface colour.
   */
  cutout?: string;
  className?: string;
}

export function LogoMark({ size = 40, color = CORAL, cutout, className }: LogoMarkProps) {
  const theme = useTheme();
  const cutoutColor = cutout ?? theme.palette.brand.surface;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Pintravel"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <path d={PIN_PATH} fill={color} stroke={color} strokeWidth={7} strokeLinejoin="round" />
      <path d={PLANE_PATH} transform={PLANE_TRANSFORM} fill={cutoutColor} />
    </svg>
  );
}

// -----------------------------------------------------------------------------
// Wordmark
// -----------------------------------------------------------------------------

export interface WordmarkProps {
  /** Font size in px. In the lockup this is ~60% of the mark height. */
  size?: number;
  /** Colour of "TRAVEL". Defaults to the theme's text colour. */
  color?: string;
  /**
   * Colour of "PIN". Defaults to coral — but to the lighter coral in dark mode,
   * because #E85D3F on navy reads dimmer than the white "TRAVEL" beside it and
   * the two halves stop looking like one word. Pass an explicit value when the
   * mark sits on a coral surface, where coral-on-coral would vanish.
   */
  pinColor?: string;
  sx?: SxProps<Theme>;
}

export function Wordmark({ size = 20, color, pinColor, sx }: WordmarkProps) {
  const theme = useTheme();
  const travelColor = color ?? theme.palette.brand.text;
  const pin = pinColor ?? (theme.palette.mode === 'dark' ? CORAL_ON_DARK : CORAL);

  return (
    <Box
      component="span"
      sx={[
        {
          fontFamily: 'Poppins, sans-serif',
          fontWeight: 700,
          fontSize: `${size}px`,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          userSelect: 'none',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box component="span" sx={{ color: pin }}>
        Pin
      </Box>
      <Box component="span" sx={{ color: travelColor }}>
        travel
      </Box>
    </Box>
  );
}

// -----------------------------------------------------------------------------
// Lockup — mark + wordmark, horizontal
// -----------------------------------------------------------------------------

export interface LogoLockupProps {
  /** Height of the mark in px; the wordmark and gap scale off it. */
  size?: number;
  /** Colour of "TRAVEL" and, when `mono` is set, of the mark too. */
  color?: string;
  /** Colour of "PIN" — see WordmarkProps. */
  pinColor?: string;
  /** Cutout colour for the mark — defaults to the theme surface. */
  cutout?: string;
  /** Render the mark in `color` instead of coral (print / watermark). */
  mono?: boolean;
  sx?: SxProps<Theme>;
}

export function LogoLockup({
  size = 40,
  color,
  pinColor,
  cutout,
  mono = false,
  sx,
}: LogoLockupProps) {
  return (
    <Box
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          // Spec: gap ≈ 1/3 of the mark height.
          gap: `${Math.round(size / 3)}px`,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <LogoMark size={size} color={mono && color ? color : CORAL} cutout={cutout} />
      {/* Spec: text height ≈ 60% of mark height. */}
      <Wordmark size={Math.round(size * 0.6)} color={color} pinColor={pinColor} />
    </Box>
  );
}

export default LogoLockup;
